/**
 * useOtherUserProfile — loads another member's profile by GUID.
 *
 * GET /UserProfile/GetOtherUserProfile answers 200 with `isAccessible: false`
 * (or `success: false`) when the owner's privacy setting blocks the viewer, so
 * a restriction is not an HTTP error and has to be normalised here rather than
 * in a screen.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import profileService from '../api/services/profileService';
import { mapApiProfileToProfile } from '../utils/userProfileMapper';
import { getApiErrorMessage } from '../utils/apiErrorHandler';
import { getProfileRestrictionReason, resolveProfileRestriction } from '../utils/profileVisibility';
import { normalizeUserGuid } from '../utils/navigateToUserProfile';
import type { ProfileRestrictionReason } from '../utils/profileVisibility';
import type { ApiError } from '../types/api';
import type { Profile } from '../types/profile';
import type { UserProfileResponse } from '../types/userProfile';

/**
 * Keeps the public identity and relationship fields an inaccessible-profile
 * response may expose, without retaining its private profile content.
 */
export const mapRestrictedProfileIdentity = (
  raw: UserProfileResponse | null | undefined,
): Profile | null => {
  if (!raw) return null;
  const hasIdentity = Boolean(
    raw.userId
    || raw.userGuid
    || raw.firstName?.trim()
    || raw.lastName?.trim()
    || raw.userName?.trim(),
  );
  if (!hasIdentity) return null;

  const mapped = mapApiProfileToProfile(raw);
  return {
    id: mapped.id,
    userId: mapped.userId,
    userGuid: mapped.userGuid,
    name: mapped.name,
    firstName: mapped.firstName,
    lastName: mapped.lastName,
    userName: mapped.userName,
    avatar: mapped.avatar,
    profilePicturePrivacy: mapped.profilePicturePrivacy,
    profileViewPrivacy: mapped.profileViewPrivacy,
    isBuddy: mapped.isBuddy,
    invited: mapped.invited,
    isRequested: mapped.isRequested,
    isAccepted: mapped.isAccepted,
    isFollowing: mapped.isFollowing,
    status: mapped.status,
    // The buddy count is public (Facebook-style); other stats stay private.
    buddiesCount: mapped.buddiesCount,
  };
};

export interface UseOtherUserProfileResult {
  profile: Profile | null;
  loading: boolean;
  /** Transport/server failure message. Empty when the load succeeded. */
  error: string;
  /** Set when the profile exists but the viewer may not see it. */
  restriction: ProfileRestrictionReason | null;
  refetch: (options?: { silent?: boolean }) => void;
}

/**
 * An inaccessible response with a public identity still represents an existing
 * account. Privacy metadata is optional on these sparse responses, so do not
 * mislabel that member as deleted merely because the exact privacy level is
 * absent.
 */
export const resolveInaccessibleProfileRestriction = (
  raw: UserProfileResponse | null | undefined,
  restrictedProfile: Profile | null,
): ProfileRestrictionReason => {
  const message = raw?.message?.trim().toLowerCase() || '';
  if (/not found|does not exist|deleted|no longer available/.test(message)) {
    return 'unavailable';
  }

  const reason = getProfileRestrictionReason(
    raw?.profileViewPrivacy,
    { isBuddy: raw?.isBuddy },
  );
  if (reason) return reason;
  return restrictedProfile ? 'restricted' : 'unavailable';
};

/** A background reload must keep the current privacy gate in place. */
export const shouldResetRestrictionBeforeProfileReload = (
  silent: boolean,
): boolean => !silent;

export const useOtherUserProfile = (
  profileUserGuid: string | null | undefined,
): UseOtherUserProfileResult => {
  const guid = normalizeUserGuid(profileUserGuid);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(Boolean(guid));
  const [error, setError] = useState('');
  const [restriction, setRestriction] = useState<ProfileRestrictionReason | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const silentReloadRef = useRef(false);

  const refetch = useCallback((options?: { silent?: boolean }) => {
    silentReloadRef.current = options?.silent === true;
    setReloadToken(token => token + 1);
  }, []);

  useEffect(() => {
    if (!guid) {
      setProfile(null);
      setLoading(false);
      setRestriction('unavailable');
      return;
    }

    let cancelled = false;
    const silentReload = silentReloadRef.current;
    silentReloadRef.current = false;
    if (shouldResetRestrictionBeforeProfileReload(silentReload)) {
      setLoading(true);
      setRestriction(null);
    }
    setError('');

    profileService
      .getOtherUserProfile(guid)
      .then(response => {
        if (cancelled) return;
        const body = response?.data;

        if (!body || body.success === false || body.isAccessible === false) {
          // Some inaccessible responses still include a public identity subset.
          // Preserve only that subset for the restricted-profile header.
          const restrictedProfile = mapRestrictedProfileIdentity(body);
          setProfile(restrictedProfile);
          setRestriction(
            resolveInaccessibleProfileRestriction(body, restrictedProfile),
          );
          setLoading(false);
          return;
        }

        const mapped = mapApiProfileToProfile(body);
        // The server is the authority: an explicit isAccessible: true means
        // the viewer (e.g. a buddy of a "Private" profile) may open it. The
        // local rule only runs when the payload omits the flag.
        setRestriction(
          resolveProfileRestriction(body.isAccessible, mapped.profileViewPrivacy, {
            isBuddy: mapped.isBuddy,
          }),
        );
        setProfile(mapped);
        setLoading(false);
      })
      .catch(caught => {
        if (cancelled) return;
        const status = (caught as ApiError & { response?: { status?: number } })
          ?.response?.status;
        if (status === 404) {
          setProfile(null);
          setRestriction('unavailable');
        } else if (!silentReload) {
          setProfile(null);
          setError(getApiErrorMessage(caught as ApiError));
        }
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [guid, reloadToken]);

  return { profile, loading, error, restriction, refetch };
};

export default useOtherUserProfile;
